import type { Ref } from 'vue'

/**
 * 编辑器模型加载态协调器配置。
 */
export interface EditorModelLoadingCoordinatorOptions {
  /** 当前是否显示模型加载遮罩。 */
  readonly isLoading: Ref<boolean>
  /** 当前模型加载遮罩文案。 */
  readonly loadingText: Ref<string>
  /** 当前持有加载遮罩的业务归属。 */
  readonly owner: Ref<string>
  /** 没有业务归属时使用的全局归属。 */
  readonly globalOwner: string
}

/**
 * 编辑器模型加载态协调器。
 *
 * 设计说明：
 * 1. OpenProject、默认模型加载、tab 切换、导入导出都会争用同一个 viewport overlay；
 * 2. 只有当前 owner 能关闭或更新自己的 loading，避免旧请求结束时误关新模型加载态；
 * 3. cache-hit 等已确认需要清零的路径可以通过 `reset` 直接归零，由调用方显式承担语义。
 */
export class EditorModelLoadingCoordinator {
  private readonly options: EditorModelLoadingCoordinatorOptions

  /**
   * 创建编辑器模型加载态协调器。
   *
   * @param options 协调器依赖的响应式状态。
   */
  constructor(options: EditorModelLoadingCoordinatorOptions) {
    this.options = options
  }

  /**
   * 显示模型加载遮罩，并把遮罩归属切换给当前 owner。
   *
   * @param text 加载遮罩文案。
   * @param owner 加载遮罩归属；未传入时使用全局归属。
   */
  show(text: string, owner?: string): void {
    this.options.owner.value = this.resolveOwner(owner)
    this.options.isLoading.value = true
    this.options.loadingText.value = text
  }

  /**
   * 仅在 owner 仍然持有遮罩时更新文案。
   *
   * @param owner 期望持有遮罩的归属。
   * @param text 新加载文案。
   */
  updateText(owner: string, text: string): void {
    if (this.options.owner.value !== owner) {
      return
    }
    this.options.loadingText.value = text
  }

  /**
   * 仅在 owner 仍然持有遮罩时关闭加载态。
   *
   * @param owner 加载遮罩归属；未传入时使用全局归属。
   */
  hide(owner?: string): void {
    const resolvedOwner = this.resolveOwner(owner)
    if (resolvedOwner !== this.options.owner.value) {
      return
    }
    this.reset()
  }

  /**
   * 权威清零当前加载态。
   *
   * @param owner 语义归属；该参数仅用于调用方表达意图，清零本身不做 owner 校验。
   */
  reset(owner?: string): void {
    this.options.isLoading.value = false
    this.options.loadingText.value = ''
    this.options.owner.value = this.options.globalOwner
    void owner
  }

  /**
   * 判断当前遮罩是否仍由指定 owner 持有。
   *
   * @param owner 需要确认的归属。
   * @returns 当前 owner 与传入 owner 一致时返回 true。
   */
  isOwnedBy(owner: string): boolean {
    return this.options.owner.value === owner
  }

  /**
   * 解析可选 owner。
   *
   * @param owner 调用方传入的归属。
   * @returns 有效归属。
   */
  private resolveOwner(owner?: string): string {
    return owner ?? this.options.globalOwner
  }
}
